<template>
  <v-card :style="[{
    'position': sticky ? 'sticky' : 'relative',
    'top': sticky ? '30px' : '0',
  }]" :flat="flat">

    <template v-if="!hideTitles">
      <v-card-text>
        Resumen de atención
        <v-spacer></v-spacer>
      </v-card-text>
      <v-divider></v-divider>
    </template>

    <v-card-text v-if="noData" class="text-center">
      <div class="caption">Aún no se han agregado datos a la consulta</div>
    </v-card-text>

    <template v-else>

      <div id="printSection">

        <div class="print-btn text-right absolute right top ma-2">
          <v-btn @click="printSummary" small depressed fab :disabled="!hasPrintableItems">
            <v-icon>mdi-printer</v-icon>
          </v-btn>
        </div>

        <div class="mt-2"></div>

        <v-sheet :height="height" style="overflow: auto">
          <div v-if="event && event.reason_description" :class="{'printable-section': true, print: printableItems.reasonDescription}">
            <v-card-title class="pb-1" v-if="!hideTitles">
              <div class="title">
                <v-checkbox
                    class="ml-2 print-check"
                    label="Pre Ingreso"
                    v-model="printableItems.reasonDescription" />
              </div>
            </v-card-title>
            <v-card-text class="py-1">
              <div class="section py-1 pl-2 secondary-card">
                <div class="body-1 font-weight-bold">Motivo de la Cita:</div>
                <div class="pa-1 list-item">
                  {{ event.reason_description }}.
                </div>
              </div>
            </v-card-text>
          </div>

          <v-slide-y-transition>
            <template v-show="comments">
              <div v-if="comments" :class="{'printable-section': true, print: printableItems.comments}">
                <v-card-title class="pb-1" v-if="!hideTitles">
                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Comentarios"
                        v-model="printableItems.comments" />
                  </div>
                </v-card-title>
                <v-card-text class="py-1">
                  <div class="section py-1 pl-2 secondary-card">
                    <div class="pa-1 list-item" v-html="comments"></div>
                  </div>
                </v-card-text>
              </div>
            </template>
          </v-slide-y-transition>

          <v-slide-y-transition>
            <template v-show="medicalHistory && medicalHistory.length > 0">
              <div v-if="isClinicalSpecialist && medicalHistory && medicalHistory.length > 0"
                   :class="{'printable-section': true, print: printableItems.medicalHistory}">
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Historia Clínica"
                        v-model="printableItems.medicalHistory" />
                  </div>

                  <v-spacer></v-spacer>

                  <v-card-actions>
                  </v-card-actions>
                </v-card-title>
                <v-card-text class="py-1">
                  <template v-for="category in medicalHistory">
                    <div class="section py-1 pl-2 secondary-card" :key="category.id">
                      <div class="body-1 font-weight-bold">{{ category.description }}:</div>
                      <div class="pa-1 list-item">
                        {{ category.items.join(', ') }}.
                      </div>
                    </div>
                  </template>
                </v-card-text>
                <v-divider></v-divider>
              </div>
            </template>
          </v-slide-y-transition>


          <v-slide-y-transition>
            <template v-show="anamnesis">
              <div v-if="anamnesis" :class="{'printable-section': true, print: printableItems.anamnesis}">
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Enfermedad Actual"
                        v-model="printableItems.anamnesis" />
                  </div>

                </v-card-title>
                <v-card-text class="py-1">
                  <div v-if="anamnesis.comment" class="section py-1 pl-2 secondary-card">
                    <div class="body-1 font-weight-bold">Motivo de Consulta:</div>
                    <div class="pa-1 list-item">{{ anamnesis.comment }}</div>
                  </div>
                  <template v-for="symptom in anamnesis.symptoms">
                    <div class="section py-1 pl-2 secondary-card" :key="symptom.id">
                      <div class="body-1 ml-1"><span class="font-weight-bold">Síntoma:</span> {{ symptom.description }}
                      </div>
                      <div class="pa-1 list-item">
                        <span class="font-weight-bold">Duración:</span> {{ symptom.duration }} {{ symptom.duration_unit }}
                        <template v-if="symptom.eye">
                          , <span class="font-weight-bold">Ojo: </span> {{ symptom.eye }}
                        </template>
                      </div>
                    </div>
                  </template>
                  <template v-for="option in anamnesis.anamnesis_options">
                    <div class="section py-1 pl-2 secondary-card" :key="option.id">
                      <div class="pa-1 list-item">
                        <span class="font-weight-bold">Observación:</span>
                        {{ option.description }}
                      </div>
                    </div>
                  </template>
                </v-card-text>
                <v-divider></v-divider>
              </div>
            </template>
          </v-slide-y-transition>


          <v-slide-y-transition>
            <div v-show="physicalExams.length > 0" :class="{'printable-section': true, print: printableItems.physicalExams}">
              <div>
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        :label="getPhysicalExamName"
                        v-model="printableItems.physicalExams" />
                  </div>

                </v-card-title>
                <v-card-text class="py-1">
                  <div v-for="exam in physicalExams">

                    <div class="section py-1 pl-2 secondary-card" :key="exam.id">

                      <div class="body-1 ml-1">
                        {{ exam.name }}
                      </div>
                      <div class="pa-1 list-item" v-for="(group, groupName) in exam.groups">
                        <template v-if="groupName === 'ungrouped'">
                          <div v-for="field in group.fields">
                            <span class="font-weight-bold">{{ field.label }}:</span> {{ field.value }}
                          </div>
                        </template>

                        <template v-else>
                          <div class="item-group">
                            <div class="body-1 ">
                              {{ groupName }}
                            </div>
                            <span v-for="field in group.fields">
                            <span class="font-weight-bold ml-1">{{ field.label }}:</span> {{ field.value }}
                          </span>
                          </div>
                        </template>

                      </div>

                    </div>
                  </div>
                </v-card-text>
              </div>
              <v-divider></v-divider>
            </div>
          </v-slide-y-transition>

          <v-slide-y-transition>
            <template v-show="event && event.diagnoses && event.diagnoses.length > 0">
              <div v-if="event && event.diagnoses && event.diagnoses.length > 0" :class="{'printable-section': true, print: printableItems.diagnosis}">
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Diagnóstico"
                        v-model="printableItems.diagnosis" />
                  </div>

                </v-card-title>
                <v-card-text class="py-1">
                  <template v-for="diagnosis in event.diagnoses">
                    <div class="section py-1 pl-2 secondary-card" :key="diagnosis.id">
                      <div class="pa-1 list-item">
                        {{ diagnosis.name }}
                      </div>
                    </div>
                  </template>
                </v-card-text>
                <v-divider></v-divider>
              </div>
            </template>
          </v-slide-y-transition>


          <v-slide-y-transition>
            <template v-show="indications">
              <div v-if="indications" :class="{'printable-section': true, print: printableItems.indications}">
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Indicaciones"
                        v-model="printableItems.indications" />
                  </div>

                </v-card-title>
                <v-card-text class="py-1">

                  <template v-if="indications.glasses">
                    <div class="section py-1 pl-2 secondary-card">
                      <div class="body-1 ml-1"><span class="font-weight-bold">Lentes:</span></div>
                      <div class="pa-1 mt-2 list-item">
                        OD
                        <span class="font-weight-bold ml-1">Sph:</span> {{ indications.glasses.od_sph }}
                        <span class="font-weight-bold ml-1">Cyl:</span> {{ indications.glasses.od_cyl }}
                        <span class="font-weight-bold ml-1">Eje:</span> {{ indications.glasses.od_degrees }}
                      </div>
                      <div class="pa-1 list-item">
                        OI
                        <span class="font-weight-bold ml-1">Sph:</span> {{ indications.glasses.oi_sph }}
                        <span class="font-weight-bold ml-1">Cyl:</span> {{ indications.glasses.oi_cyl }}
                        <span class="font-weight-bold ml-1">Eje:</span> {{ indications.glasses.oi_degrees }}
                      </div>

                      <div class="body-1 my-2">
                        <span class="font-weight-bold ml-1">Add. cerca:</span> {{ indications.glasses.add }}
                        <span class="font-weight-bold ml-1">DP:</span> {{ indications.glasses.dp }}
                      </div>
                    </div>
                  </template>

                  <template v-for="medicine in indications.medicines">
                    <div class="section py-1 pl-2 secondary-card" :key="medicine.id">
                      <div class="body-1 ml-1"><span class="font-weight-bold">Medicamento:</span> {{ medicine.name }}
                      </div>
                      <div class="pa-1 list-item">
                        <span class="font-weight-bold">Cada:</span> {{ medicine.interval }}
                        hora{{ medicine.interval > 1 ? 's' : '' }}
                        <span class="font-weight-bold">Durante:</span> {{ medicine.duration }}
                        {{ medicine.duration_unit }}
                      </div>
                      <div class="body-1 ml-1" v-if="medicine.comment">
                        <span class="font-weight-bold">Comentarios: </span> {{ medicine.comment }}
                      </div>
                    </div>
                  </template>

                  <template v-if="complementaryExams.length > 0">
                    <div class="section py-1 pl-2 secondary-card">
                      <div class="body-1 ml-1"><span class="font-weight-bold">Exámenes complementarios:</span></div>
                      <div class="pa-1 list-item">
                        {{ complementaryExams.join(', ') }}.
                      </div>
                    </div>
                  </template>

                  <template>
                    <div class="section py-1 pl-2 secondary-card">
                      <div class="body-1 ml-1"><span class="font-weight-bold">Control:</span>
                        {{ indications && indications.control && indications.control.required ? `Dentro de ${indications.control.duration} ${pluralDurationUnits[indications.control.duration_unit]}` : 'No requiere' }}
                      </div>
                    </div>
                  </template>

                </v-card-text>
              </div>
            </template>
          </v-slide-y-transition>

          <v-slide-y-transition>

            <div v-if="!!psychologicalRecord" >

              <div :class="{'printable-section': true, print: printableItems.technical_reason}">
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Motivo Latente"
                        v-model="printableItems.technical_reason" />
                  </div>

                </v-card-title>

                <v-card-text class="py-1">
                  <div class="section py-1 pl-2 secondary-card">
                    <div class="pa-1 list-item" v-html="psychologicalRecord.technical_reason"></div>
                  </div>
                </v-card-text>

              </div>

              <div :class="{'printable-section': true, print: printableItems.information}">
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Información"
                        v-model="printableItems.information" />
                  </div>

                </v-card-title>

                <v-card-text class="py-1">

                  <template v-if="event.client.is_adult === false">

                    <div class="section py-1 pl-2 secondary-card">
                      <div class="body-1 ml-1">
                        <span class="font-weight-bold">Datos Relevantes de Salud:</span>
                      </div>
                      <div class="pa-1 list-item" v-html="psychologicalRecord.relevant_health_info"></div>
                    </div>

                    <div class="section py-1 pl-2 secondary-card">
                      <div class="body-1 ml-1">
                        <span class="font-weight-bold">Datos Relevantes Escolares:</span>
                      </div>
                      <div class="pa-1 list-item" v-html="psychologicalRecord.relevant_school_info"></div>
                    </div>

                    <div class="section py-1 pl-2 secondary-card">
                      <div class="body-1 ml-1">
                        <span class="font-weight-bold">Datos Relevantes Familiares:</span>
                      </div>
                      <div class="pa-1 list-item" v-html="psychologicalRecord.relevant_family_info"></div>
                    </div>

                  </template>

                  <template v-else>
                    <div class="section py-1 pl-2 secondary-card">
                      <div class="body-1 ml-1">
                        <span class="font-weight-bold">Datos Relevantes:</span>
                      </div>
                      <div class="pa-1 list-item" v-html="psychologicalRecord.relevant_health_info"></div>
                    </div>
                  </template>

                </v-card-text>
              </div>

              <div :class="{'printable-section': true, print: printableItems.clinical_comments}">
                <v-card-title class="pb-1" v-if="!hideTitles">

                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Apreciación Clínica"
                        v-model="printableItems.clinical_comments" />
                  </div>

                </v-card-title>

                <v-card-text class="py-1">
                  <div class="section py-1 pl-2 secondary-card">
                    <div class="pa-1 list-item" v-html="psychologicalRecord.clinical_comments"></div>
                  </div>
                </v-card-text>
              </div>

              <div :class="{'printable-section': true, print: printableItems.psychological_indications}">
                <v-card-title class="pb-1" v-if="!hideTitles">
                  <div class="title">
                    <v-checkbox
                        class="ml-2 print-check"
                        label="Pasos a Seguir"
                        v-model="printableItems.psychological_indications" />
                  </div>
                </v-card-title>

                <v-card-text class="py-1">
                  <div class="section py-1 pl-2 secondary-card">
                    <div class="pa-1 list-item" v-html="psychologicalRecord.indications"></div>
                  </div>
                </v-card-text>
              </div>


              <template v-if="psychologicalRecord.session_number !== 0">

                <div :class="{'printable-section': true, print: printableItems.session_comments}">

                  <v-card-title class="pb-1" v-if="!hideTitles">
                    <div class="title">
                      <v-checkbox
                          class="ml-2 print-check"
                          label="Observaciones de la Sesión"
                          v-model="printableItems.session_comments" />
                    </div>

                  </v-card-title>

                  <v-card-text class="py-1">
                    <div class="section py-1 pl-2 secondary-card">
                      <div class="pa-1 list-item" v-html="psychologicalRecord.session_comments"></div>
                    </div>
                  </v-card-text>
                </div>

              </template>
              <v-divider></v-divider>

            </div>
          </v-slide-y-transition>

          <v-slide-y-transition>
            <div v-show="!!event.steps" :class="{'printable-section': true, print: printableItems.steps}">
              <v-card-title class="pb-1" v-if="!hideTitles">
                <div class="title">
                  <v-checkbox
                      class="ml-2 print-check"
                      label="Pasos a Seguir"
                      v-model="printableItems.steps" />
                </div>

              </v-card-title>
              <v-card-text class="py-1">
                <div class="section py-1 pl-2 secondary-card">
                  <div class="pa-1 list-item" v-html="steps"></div>
                </div>
              </v-card-text>
            </div>
          </v-slide-y-transition>
        </v-sheet>


      </div>

    </template>

  </v-card>
</template>
<script>
import {getPhysicalExamName} from "@/utils/Strings";
import {isClinicalSpecialist} from "@/utils/Specialist";

export default {
  props: {
    height:{
      type: String,
      default: 'auto'
    },
    sticky:{
      type: Boolean,
      default: false
    },
    flat: {
      type: Boolean,
      default: false
    },
    event: {
      validator: prop => typeof prop === 'object' || prop === null,
      required: true
    },
    hideTitles: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      printableItems: {
        reasonDescription: false,
        medicalHistory: false,
        anamnesis: false,
        physicalExams: false,
        diagnosis: false,
        indications: false,
        technical_reason: false,
        information: false,
        psychological_indications: false,
        session_comments: false,
        steps: false,
        comments: false
      },

    }
  },
  computed: {
    getPhysicalExamName,
    isClinicalSpecialist,
    hasPrintableItems() {
      const vm = this
      for (let attr in vm.printableItems) {
        if (vm.printableItems[attr]) {
          return true
        }
      }

      return false

    },
    steps() {
      const vm = this
      return vm.event && vm.event.steps && vm.event.steps.replace(/\n/g, '<br/>') || ''
    },
    comments(){
      const vm = this
      if (!vm.event || !vm.event.comments) return ''
      return vm.event.comments.replace(/\n/g, '<br/>')
    },
    physicalExams() {
      var vm = this
      if (
          !vm.event ||
          !vm.event.physical_exams ||
          vm.event.physical_exams.length === 0
      ) return []

      var exams = []

      for (let i in vm.event.physical_exams) {

        var exam = vm.event.physical_exams[i]
        var groups = {}
        const fields = exam.pivot.values

        for (let i in fields) {
          var field = fields[i]
          var groupName = field.group && field.group.trim() || 'ungrouped'

          if (!groups[groupName]) {
            groups[groupName] = {fields: []}
          }

          field.originalIndex = i
          groups[groupName].fields.push(field)

        }

        exams.push({
          id: exam.id,
          name: exam.name,
          groups: groups
        })

      }

      return exams

    },
    anamnesis() {
      var vm = this
      if (!vm.event || !vm.event.anamnesis ||
          (
              vm.event.anamnesis && vm.event.anamnesis.symptoms.length === 0 &&
              vm.event.anamnesis.anamnesis_options.length === 0 &&
              !vm.event.anamnesis.comment
          )

      ) return null


      var anamnesis = JSON.parse(JSON.stringify(vm.event.anamnesis))
      anamnesis.symptoms = anamnesis.symptoms.map(symptom => {
        const eye = symptom.pivot.eye ? (
            symptom.pivot.eye === 'left' ? 'izquierdo' :
                (symptom.pivot.eye === 'right' ? 'derecho' : 'ambos')
        ) : null
        return {
          description: symptom.description,
          duration: symptom.pivot.duration,
          duration_unit: vm.durationUnits[symptom.pivot.duration_unit],
          eye: eye
        }
      })
      return anamnesis

    },
    complementaryExams() {
      var vm = this

      if (
          !vm.event ||
          !vm.event.indication ||
          !vm.event.indication.complementary_exams ||
          vm.event.indication.complementary_exams.length === 0
      ) return []

      const allCategories = JSON.parse(JSON.stringify(vm.event.indication.complementary_exams))

      return allCategories
          .map(cat => {
            return cat.description
          })
    },
    medicalHistory() {
      var vm = this

      if (
          !vm.event ||
          !vm.event.medical_history_items ||
          vm.event.medical_history_items.length === 0
      ) return []

      const allCategories = JSON.parse(JSON.stringify(vm.event.medical_history_items))

      return allCategories
          .filter(cat => cat.items && cat.items.length > 0)
          .map(cat => {
            cat.items = cat.items.map(item => item.description)
            return cat
          })
    },
    indications() {
      var vm = this

      if (
          !vm.event ||
          !vm.event.indication
      ) return null

      var indications = JSON.parse(JSON.stringify(vm.event.indication))
      indications.medicines = indications.medicines && indications.medicines.map(medicine => {
        return {
          name: medicine.name,
          dose: medicine.pivot.dose,
          interval: medicine.pivot.interval,
          duration: medicine.pivot.duration,
          duration_unit: vm.pluralDurationUnits[medicine.pivot.duration_unit],
          comment: medicine.pivot.comment
        }
      }) || []


      if(
          indications.complementary_exams.length === 0
          && !indications.control
          && !indications.glasses
          && indications.medicines.length === 0
      ) return null

      return indications

    },
    noData() {
      var vm = this
      return (
          !vm.psychologicalRecord &&
          ((vm.isClinicalSpecialist && vm.medicalHistory.length === 0) || !vm.isClinicalSpecialist) &&
          vm.complementaryExams.length === 0 &&
          vm.physicalExams.length === 0 &&
          !vm.indications &&
          !vm.comments &&
          (!vm.anamnesis ||
              (
                  vm.anamnesis && vm.anamnesis.symptoms.length === 0 &&
                  vm.anamnesis.anamnesis_options.length === 0 &&
                  !vm.anamnesis.comment
              )
          )
      )
    },
    durationUnits() {
      return {
        'year': 'año(s)',
        'month': 'mes(es)',
        'week': 'semana(s)',
        'day': 'día(s)',
        'hour': 'hora(s)'
      }
    },
    pluralDurationUnits() {
      return {
        'years': 'año(s)',
        'months': 'mes(es)',
        'weeks': 'semana(s)',
        'days': 'día(s)',
        'hours': 'hora(s)'
      }
    },
    psychologicalRecord() {

      var vm = this
      if (!vm.event || !vm.event.psychological_record) return null

      const {
        technical_reason,
        relevant_health_info,
        relevant_school_info,
        relevant_family_info,
        session_number,
        session_comments,
        clinical_comments,
        indications,
      } = vm.event.psychological_record

      return {
        technical_reason: vm.encodeLineBreaks(technical_reason),
        relevant_health_info: vm.encodeLineBreaks(relevant_health_info),
        relevant_school_info: vm.encodeLineBreaks(relevant_school_info),
        relevant_family_info: vm.encodeLineBreaks(relevant_family_info),
        session_number: session_number,
        session_comments: vm.encodeLineBreaks(session_comments),
        clinical_comments: vm.encodeLineBreaks(clinical_comments),
        indications: vm.encodeLineBreaks(indications),
      }

    }
  },
  methods: {
    printSummary(){
      this.$htmlToPaper("printSection");
    },
    encodeLineBreaks(str = '', defaultStr = '-') {
      return str ? str.replace(/\n/g, '<br>') : defaultStr
    }
  }
}
</script>

<style scoped>

.print-check {
  height: 32px;
  margin: 0;
  font-weight: bold;
}

.section {
  border-radius: 5px;
  margin: 3px;
}

.item-group {
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #ccc;
  margin-right: 5px;
}

.list-item {

}
</style>
